import { CheckCircle } from "@mui/icons-material";
import { Alert, Backdrop, CircularProgress, Grid } from "@mui/material";
import { createReport } from "components/api/createReport";
import { ensureAnyPortalAdmins } from "components/api/roles";
import { getServicePrincipal } from "components/api/servicePrincipal";
import { PrimaryButton } from "components/Common/Buttons/Button";
import { PrimaryDialog } from "components/Common/Dialogs/Dialog";
import {
	selectIsDataGenerationComplete,
	selectReportGenerationHasBeenStarted,
} from "features/reports";
import { selectRoles, setRoles } from "features/roles";
import { setShouldOpenTenantDrawerOnEnterPortal } from "features/setupDialog";
import { useAppSelector, useAuth } from "hooks/hooks";
import { useEffect, useState } from "react";
import { handleLogout as standardHandleLogout } from "utilities/handleLogout";
import { clearPreviouslySetAccessTokens } from "utilities/tokenUtils/sessionStorageHelper";
import { DataGenerationStatusView } from "./DataGenerationStatuses/DataGenerationStatuses";
import styles from "./SettingUpPortalDialog.module.scss";

interface SettingUpPortalDialogProps {
	isMissingPermissions: boolean;
	userRoles: {
		userHasCorrectRoles: boolean;
		checkingUserRoles: boolean;
	};
	setOpenSettingUpPortalDialog: (open: boolean) => void;
	setPortalIsInTenant: (inTenant: boolean) => void;
	setAnyPortalAdmins: (hasAdmins: boolean) => void;
	isForceSetup?: boolean;
	setHasUserEnteredPortal?: (hasEntered: boolean) => void; // New prop
}

const SettingUpPortalDialog = ({
	isMissingPermissions,
	userRoles,
	setAnyPortalAdmins,
	setOpenSettingUpPortalDialog,
	setPortalIsInTenant,
	isForceSetup = false,
	setHasUserEnteredPortal,
}: SettingUpPortalDialogProps) => {
	const { auth, dispatch } = useAuth();
	const { instance, account, authConfig } = auth;
	const roles = useAppSelector(selectRoles);

	// In order to let the user focus solely on the dialog, we hide the rest of the page
	const [timeElapsed, setTimeElapsed] = useState<number>(0);
	const isLoadingPermissions =
		timeElapsed <= 5 || isMissingPermissions || userRoles.checkingUserRoles;
	const isLoadingAdminRights = timeElapsed <= 30 || !roles?.includes("Admin");

	const [portalHasAccess, setPortalHasAccess] = useState<boolean>(false);
	const [isPreparingData, setIsPreparingData] = useState<boolean>(false);
	const [hasTriggeredCreateReport, setHasTriggeredCreateReport] = useState<boolean>(false);

	const disableActions = isLoadingPermissions || isLoadingAdminRights || isPreparingData;

	const dataGenerationHasBeenStarted = useAppSelector(selectReportGenerationHasBeenStarted);

	useEffect(() => {
		const ensureSetupInterval = setInterval(() => {
			setTimeElapsed((timeElapsed) => timeElapsed + 1);
		}, 1000);
		const ensureSetupReady = async () => {
			const servicePrincipal = await getServicePrincipal({
				instance,
				account,
				authConfig,
				appId: instance.getConfiguration().auth.clientId,
			});

			if (!servicePrincipal) {
				return;
			}

			setPortalHasAccess(true);

			const portalAdmins = await ensureAnyPortalAdmins(instance, account, authConfig);

			if (!portalAdmins) {
				return;
			}

			// Force update admin rights of logged in user once portal admins are set
			dispatch(setRoles(["Admin"]));

			setAnyPortalAdmins(true);
		};

		// Try to ensure setup is ready each 5 seconds if portal admins is not set
		// OR if we're forcing setup, run it immediately and every 5 seconds
		if (
			((timeElapsed % 5 === 0 || timeElapsed === 5) && // check first after 5 seconds, then every 5 seconds
				!roles?.includes("Admin") && // don't check if user already has admin rights
				!userRoles.checkingUserRoles && // if directory roles check is finished
				userRoles.userHasCorrectRoles) || // and the user has correct roles
			(isForceSetup && (timeElapsed % 5 === 0 || timeElapsed === 1)) // or if forcing setup, run immediately and every 5 seconds
		) {
			// When forcing setup, don't set anyPortalAdmins to true as it will hide the dialog
			if (isForceSetup) {
				// Only set admin roles, don't set anyPortalAdmins to true
				if (!roles?.includes("Admin")) {
					dispatch(setRoles(["Admin"]));
				}
			} else {
				ensureSetupReady();
			}
		}

		return () => clearInterval(ensureSetupInterval);
	}, [timeElapsed, roles, userRoles, isForceSetup, instance, account, authConfig]);

	// Use the forceSetup prop passed from parent component
	useEffect(() => {
		// If we're forcing setup, set portal access to true immediately
		if (isForceSetup) {
			setPortalHasAccess(true);
		}

		// Create reports if portal has access and data generation hasn't started
		if (
			((portalHasAccess && !dataGenerationHasBeenStarted) ||
				(isForceSetup && !dataGenerationHasBeenStarted && portalHasAccess)) &&
			!hasTriggeredCreateReport
		) {
			setHasTriggeredCreateReport(true);
			setIsPreparingData(true);
			createReport({
				instance,
				account,
				authConfig,
				url: authConfig.REPORTS_API_ENDPOINTS.CREATE_REPORTS(),
			});
		}
	}, [
		portalHasAccess,
		instance,
		account,
		authConfig,
		dataGenerationHasBeenStarted,
		hasTriggeredCreateReport,
		isForceSetup,
	]);

	// Set isPreparingData to true when we detect that report generation has started
	// BUT respect both completion and the 6-minute force-enable timeout
	const dataGenerationComplete = useAppSelector(selectIsDataGenerationComplete);
	useEffect(() => {
		if (
			dataGenerationHasBeenStarted &&
			!isPreparingData &&
			timeElapsed <= 360 &&
			!dataGenerationComplete
		) {
			setIsPreparingData(true);
		}
		// // Don't override when data is complete or after 6 minutes
		// else if (dataGenerationHasBeenStarted && (dataGenerationComplete || timeElapsed > 360)) {
		// }
	}, [dataGenerationHasBeenStarted, isPreparingData, timeElapsed, dataGenerationComplete]);

	// FALLBACK: Force Admin role after 30 seconds in force setup mode
	useEffect(() => {
		if (isForceSetup && timeElapsed > 30 && !roles?.includes("Admin")) {
			dispatch(setRoles(["Admin"]));
		}
	}, [isForceSetup, timeElapsed, roles, dispatch]);

	const handleCloseDialog = () => {
		// We clear the previously set access tokens, as these have old roles
		clearPreviouslySetAccessTokens();

		// Mark that user has entered portal in this session
		if (setHasUserEnteredPortal) {
			setHasUserEnteredPortal(true);
		}

		// Signal that the tenant drawer should open after setup completes
		dispatch(setShouldOpenTenantDrawerOnEnterPortal(true));

		// Immediately close the dialog and stop all polling
		setPortalIsInTenant(true);
		setOpenSettingUpPortalDialog(false);
		setAnyPortalAdmins(true); // This will prevent the dialog from reopening
	};

	const handleLogout = () => {
		// Use the standardized logout function to ensure consistent behavior
		// and proper redirect handling to avoid blank screens
		standardHandleLogout({ account, instance });
	};

	const content = () => {
		const missingUserAccess = !userRoles.checkingUserRoles && !userRoles.userHasCorrectRoles;

		if (missingUserAccess && !isForceSetup) {
			return (
				<PrimaryDialog
					title={"Missing user access"}
					primaryAction={handleLogout}
					size="medium"
					primaryButtonText={"Logout"}
					removeContentMarginAndPadding={true}
				>
					<Grid container p={3} pb={0} display={"flex"} flexDirection={"column"}>
						<Grid container direction="row" alignItems="center">
							<Grid item>
								<div>
									It looks like <b>{account?.username ?? "you"}</b> don&apos;t
									have access to configure this portal. Please check if you have
									logged in with an <b>azure global administrator </b>account or
									contact your administrator for help.
								</div>
							</Grid>
						</Grid>
					</Grid>
				</PrimaryDialog>
			);
		}

		return (
			<Grid container className={styles.fullScreenSetup}>
				<Grid container className={styles.ingressContainer}>
					{
						<>
							<p>We are making the portal ready for you, this might take some time</p>
						</>
					}
				</Grid>
				<Grid container className={styles.loadingContainer}>
					<Grid container className={styles.loadingLine}>
						<Grid item>
							{!isLoadingAdminRights && (
								<CheckCircle fontSize="large" color="success" />
							)}
							{isLoadingAdminRights && (
								<div className={styles.loadingIcon}>
									<CircularProgress size={30} color="secondary" />
								</div>
							)}
						</Grid>
						<Grid item ml={3}>
							<span>Configuring portal admin access</span>
						</Grid>
					</Grid>
					<DataGenerationStatusView
						portalHasAccess={portalHasAccess}
						setIsPreparingData={setIsPreparingData}
						timeElapsed={timeElapsed}
					/>

					{/* ✅ Add notification about re-login requirement */}
					<Alert severity="info" sx={{ mt: 2, mb: 2, maxWidth: 500 }}>
						<strong>Note:</strong> You will need to log in again after clicking the
						button below to get your full admin access.
					</Alert>

					<PrimaryButton
						size="medium"
						action={handleCloseDialog}
						isDisabled={disableActions}
						variantStyle="contained"
						text={"Enter portal"}
						marginTop={3}
					/>
				</Grid>
			</Grid>
		);
	};

	return (
		<>
			{timeElapsed > 0.3 && (
				<Backdrop open={true} className={styles.backdrop}>
					{content()}
				</Backdrop>
			)}
		</>
	);
};

export { SettingUpPortalDialog };
